<script setup lang="ts">
import { NATIVE_SOCIAL_KEY } from '~/utils/native-social-login';
import { Preferences } from '@capacitor/preferences';
import { stageNativeUpdate } from '~/utils/native-live-update';
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { useUserStore } from './store/user';

const { t, locale, locales } = useI18n()
const colorMode = useColorMode()
const userStore = useUserStore()
const platform = useCapacitorPlatform()
const config = useRuntimeConfig()
const nuxtApp = useNuxtApp()

const color = computed(() => colorMode.value === 'dark' ? '#111827' : 'white')

const { brandName, pageTitle } = useBranding()

// `language` is the BCP-47 tag on each locale in nuxt.config (pt-BR, en-GB);
// `code` is the internal id (pt-PT, en-GB) and is not what a browser reads.
const htmlLang = computed(() => {
  const active = (locales.value as any[]).find(l => l.code === locale.value)
  return active?.language || active?.code || locale.value
})

useHead({
  // Every page that sets a title gets the brand appended here, so a page never
  // has to spell it out (four of them used to, as a literal). A page with no
  // title of its own is the brand alone.
  titleTemplate: (title?: string) => pageTitle(title),
  meta: [
    { charset: 'utf-8' },
    // On Android, Capacitor 8 owns system-bar/cutout/keyboard insets. Omitting
    // viewport-fit=cover keeps fixed headers, drawers and footers inside them.
    // iOS retains its existing native contentInset handling.
    { name: 'viewport', content: platform.platform.value === 'android' ? 'width=device-width, initial-scale=1' : 'width=device-width, initial-scale=1, viewport-fit=cover' },
    { key: 'theme-color', name: 'theme-color', content: color }
  ],
  link: [
    { rel: 'icon', href: '/favicon.ico' }
  ],
  htmlAttrs: {
    // The language the page is ACTUALLY in. This was pinned to 'en' while the
    // console renders pt-BR for most customers, and a page whose declared
    // language disagrees with its text is exactly what makes Chrome and Firefox
    // offer to translate it. A browser translator rewrites text nodes in place
    // — and Vue marks the start and end of every Fragment with an EMPTY TEXT
    // NODE, so once a list has been translated `removeFragment()` walks
    // `nextSibling` straight past the anchor it is looking for and into null.
    // That crashes the whole render loop, in production only, with no
    // application frames in the stack to say where.
    lang: htmlLang,
    // And do not let a browser translate on top of it. The console ships its
    // own pt-BR and en-GB copy with a switcher, so machine translation buys a
    // third-language reader little — and it rewrites text nodes in place,
    // which is what takes the anchors above out from under Vue.
    translate: 'no'
  }
})

const description = computed(() =>
  config.public.pageDescription.length
    ? config.public.pageDescription
    : t('common.appDescription')
)

useSeoMeta({
  // No `title` here: the titleTemplate above already renders the brand for an
  // untitled page, and setting one would make every page read "Page — Brand —
  // Brand".
  description,
  ogTitle: brandName,
  ogDescription: description,
  ogImage: 'https://dashboard-template.nuxt.dev/social-card.png',
  twitterImage: 'https://dashboard-template.nuxt.dev/social-card.png',
  twitterCard: 'summary_large_image'
})

// Check for updates
const isUpdating = ref(false)
async function nativeLoginActive() {
  if (nuxtApp.$nativeSocialLogin?.isBusy()) return true
  const { value } = await Preferences.get({ key: NATIVE_SOCIAL_KEY })
  try { return !!value && JSON.parse(value).expiresAt > Date.now() }
  catch { return false }
}
const checkForUpdates = async () => {
  try {
    // A reload while returning from the browser can consume the callback and
    // discard its result. Let the next launch check for an update instead.
    if (await nativeLoginActive()) return;
    // Set the channel from where Capawesome will fetch the updates
    await LiveUpdate.setChannel({ channel: config.public.capawesomeChannel });
    isUpdating.value = true
    if (await stageNativeUpdate(LiveUpdate, nativeLoginActive)) {
      if (await nativeLoginActive()) return
      await LiveUpdate.reload()
    } else {
      console.log('No compatible updates available')
    }
  } catch (error) {
    console.error('Update check failed:', error)
  } finally {
    isUpdating.value = false
  }
}

// Default language on page load
// userStore.setUserDefaultLanguage()
onMounted(() => {
  // userStore.setBrowserDefaultLanguage()

  // Check if the app is running on a mobile device
  if (platform.isMobile.value) {
    // Check for updates when the app is running on a mobile device
    LiveUpdate.ready().then(async () => {
      await checkForUpdates()
    })
  }
})
// Watch countrycode changes — do NOT run immediately during hydration.
// setLocale() triggers i18n reactive state which causes hydration mismatches
// when called before the component tree has finished hydrating.
const countryCode = computed(() => (userStore.user as any)?.client?.countrycode)
watch(countryCode, (newCode, oldCode) => {
  if (newCode !== oldCode && newCode != undefined) {
    console.log(`Country code changed from ${oldCode} to ${newCode}`)
    userStore.setUserDefaultLanguage()
  }
})

onMounted(() => {
  // Apply user locale preference after hydration is complete
  if (countryCode.value) {
    userStore.setUserDefaultLanguage()
  }
})
</script>

<template>
  <UApp>
    <NuxtLoadingIndicator />

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <LiveUpdateLoadingScreen :show="isUpdating" />
  </UApp>
</template>
